import { ChangeEvent, FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import { TabsProvider } from "../../context/tabsContext";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useI18n } from "../../hooks/useTranslations";
import { useClientsApi } from "../../http/clients/api";
import { FullClient, UpsertClientBody } from "../../http/clients/types";
import { generateSecret } from "../../types/utils";
import SvgAdd from "../icons/Add";
import SvgClose from "../icons/Close";
import Button from "../ui/Button";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import FormFieldsContainer from "../ui/FormFieldsContainer";
import Header from "../ui/Header";
import Input from "../ui/Input";
import Panel from "../ui/Panel";
import Tabs, { TabContent, TabsContents } from "../ui/Tabs";
import styles from "./AdminClient.module.css";
import ClientPermissionsForm from "./AdminClientPermission";

export const NewAdminClient: FC = () => {
  const i18n = useI18n();

  return (
    <>
      <Header title={i18n.t.clients.createNew} />
      <NewEditAdminClient />
    </>
  );
};

export const EditAdminClient: FC = () => {
  const i18n = useI18n();

  return (
    <>
      <Header title={i18n.t.users.edit} />
      <TabsProvider>
        <TabbedEditClient />
      </TabsProvider>
    </>
  );
};

export const TabbedEditClient: FC = () => {
  const i18n = useI18n();
  const userSettings = useUserSettings();
  const { clientId } = useParams<{ clientId: string }>();
  const clientsApi = useClientsApi();
  const aborter = useRef(new AbortController());
  const dispatch = useContext(NotificationDispatch);

  const clientSwr = clientsApi.getAdminClient(clientId!, aborter.current.signal);
  const [client, isInitialized] = useDataInitializer(clientSwr.data, clientSwr.error);

  useEffect(() => {
    clientId && clientSwr.trigger();
  }, [clientId]);

  useEffect(() => {
    clientSwr.error && dispatch(showErrorNotification(i18n, clientSwr.error));
  }, [clientSwr.error]);

  const tabs = [{ title: i18n.t.common.generalData }];

  const tabComponents: TabContent[] = [
    {
      body: (
        <NewEditAdminClient
          client={client}
          disabled={!client || !userSettings.permissions.clients.update}
          isLoading={!isInitialized}
        />
      ),
      eager: true,
      withBorder: true,
    },
  ];

  if (client) {
    tabs.push({ title: i18n.t.common.permissions });

    tabComponents.push({
      body: (
        <ClientPermissionsForm
          key="role_permissions"
          client={client}
          isLoading={!isInitialized}
          isDisabled={!client || !userSettings.permissions.clients.update}
        />
      ),
      withBorder: true,
    });
  }

  return (
    <>
      <Tabs tabs={tabs} />
      <TabsContents contents={tabComponents} />
    </>
  );
};

interface AdminClientFormBody {
  name: string | null;
  secrets: {
    id: string | null;
    name: string;
    value: string;
  }[];
}

const secretLength = 32;

interface NewEditAdminClientProps {
  client?: FullClient | null;
  disabled?: boolean;
  isLoading?: boolean;
}

const NewEditAdminClient: FC<NewEditAdminClientProps> = ({ client, disabled, isLoading }: NewEditAdminClientProps) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const navigate = useNavigate();
  const clientsApi = useClientsApi();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [secretErrors, setSecretErrors] = useState<boolean[]>([]);

  const defaultValues: AdminClientFormBody = {
    name: client?.name ?? null,
    secrets:
      client?.secrets.map((x) => ({ id: x.id, name: x.name, value: "*".repeat(secretLength - 3) + x.suffix })) ?? [],
  };

  const onSubmit = () => async (formData: AdminClientFormBody) => {
    setIsSubmitting(true);

    const body: UpsertClientBody = {
      name: formData.name ?? "",
      secrets: formData.secrets,
    };

    if (client?.id) {
      try {
        await clientsApi.updateAdminClient(client.id, body);
        dispatch(showSuccessNotification(i18n.t));
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    } else {
      try {
        const res = await clientsApi.insertAdminClient(body);
        navigate(`/admin/clients/${res.id}`, { replace: true });
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const content = (
    <Form<AdminClientFormBody>
      submitText={i18n.t.common.save}
      disabled={disabled || isSubmitting}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
    >
      {({ register, formState: { errors }, reset, watch, setValue, control }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [client]);

        return (
          <FormFieldsContainer>
            <FormField label={i18n.t.common.name} error={errors.name}>
              {({ labelId, isOptional, isInvalid }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={disabled || isLoading || isSubmitting}
                  {...register("name", {
                    required: !isOptional,
                    minLength: 1,
                  })}
                />
              )}
            </FormField>
            <Controller
              name="secrets"
              control={control}
              rules={{
                validate: (values) => {
                  const [es, ok] = values.reduce(
                    (rv: [boolean[], boolean], x) => {
                      const ok = !!x.name;
                      return [[...rv[0], !ok], rv[1] && ok];
                    },
                    [[], true],
                  );
                  setSecretErrors(es);
                  return ok;
                },
              }}
              render={() => (
                <FormField label={i18n.t.secrets.plural} isOptional>
                  {({ labelId }) => (
                    <table className={styles.secrets} cellPadding={0} cellSpacing={0}>
                      <tbody>
                        {watch("secrets").map((x, index) => (
                          <tr key={`secret_${index}`}>
                            <td>
                              <Input
                                value={x.name}
                                id={`secret_name_id_${index}_${labelId}`}
                                disabled={disabled || isLoading || isSubmitting}
                                isInvalid={secretErrors[index]}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                  const a = watch("secrets").filter((_, i) => {
                                    return i != index;
                                  });
                                  a.splice(index, 0, { ...x, name: e.target.value });
                                  setValue("secrets", a);
                                }}
                              />
                            </td>
                            <td>
                              <Input value={x.value} disabled id={`secret_id_${index}`} className={styles.value} />
                            </td>
                            <td>
                              <Button
                                glyph={SvgClose}
                                buttonProps={{
                                  disabled: disabled || isLoading || isSubmitting,
                                  onClick: () => {
                                    setSecretErrors([]);
                                    setValue(
                                      "secrets",
                                      watch("secrets").filter((_, i) => index !== i),
                                    );
                                  },
                                }}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </FormField>
              )}
            />
            <div className={styles.button}>
              <Button
                glyph={SvgAdd}
                buttonProps={{
                  disabled: disabled || isLoading || isSubmitting,
                  onClick: () => {
                    setSecretErrors([]);
                    setValue("secrets", [
                      ...watch("secrets"),
                      { id: null, name: "", value: generateSecret(secretLength) },
                    ]);
                  },
                }}
              />
            </div>
          </FormFieldsContainer>
        );
      }}
    </Form>
  );

  return client ? content : <Panel>{content}</Panel>;
};
